import React, { useEffect, useRef } from 'react';
import { Chart } from 'chart.js/auto';

const MetricCard = ({ label, value, sub, subColor = 'text-gray-400', icon, iconBg }) => (
  <div className="stat-card rounded-2xl p-5 flex flex-col gap-2 hover:scale-[1.02] transition-transform duration-200">
    <div className="flex items-center justify-between">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
      <div className={`w-8 h-8 rounded-xl ${iconBg} flex items-center justify-center text-sm`}>
        <i className={`fas ${icon}`}></i>
      </div>
    </div>
    <p className="text-2xl font-bold text-gray-800 tabular-nums">{value}</p>
    <p className={`text-xs font-medium ${subColor}`}>{sub}</p>
  </div>
);

export default function CollectionsPanel() {
  const chartRef = useRef();
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!chartRef.current) return;
    if (chartInstance.current) chartInstance.current.destroy();
    chartInstance.current = new Chart(chartRef.current, {
      type: 'bar',
      data: {
        labels: ['1–30 days', '31–60 days', '61–90 days', '>90 days'],
        datasets: [{
          label: 'Outstanding ($)',
          data: [23400, 41200, 48200, 15900],
          backgroundColor: ['#bfdbfe', '#93c5fd', '#3b82f6', '#1e40af'],
          borderRadius: 8,
          borderSkipped: false,
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: ctx => ` $${ctx.raw.toLocaleString()}`
            }
          }
        },
        scales: {
          x: { grid: { display: false }, border: { display: false } },
          y: {
            grid: { color: 'rgba(0,0,0,0.04)' },
            border: { display: false },
            ticks: { callback: v => `$${(v/1000).toFixed(0)}k` }
          }
        }
      }
    });
    return () => chartInstance.current?.destroy();
  }, []);

  return (
    <div className="flex-1 overflow-y-auto p-4 md:p-6">

      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-2xl bg-blue-600 flex items-center justify-center text-white shadow-md">
          <i className="fas fa-money-bill-wave"></i>
        </div>
        <div>
          <h2 className="text-lg font-bold text-gray-800">Collections Intelligence</h2>
          <p className="text-xs text-gray-400">A/R aging analysis · AI dunning strategy</p>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
        <MetricCard label="Total Overdue >60d" value="$48,200"  sub="23 invoices"      subColor="text-red-500"   icon="fa-clock"         iconBg="bg-red-100 text-red-600" />
        <MetricCard label="High-risk customers" value="2"        sub="$46k exposure"   subColor="text-amber-500" icon="fa-user-xmark"    iconBg="bg-amber-100 text-amber-600" />
        <MetricCard label="DSO (days)"          value="52"       sub="−4 vs last month" subColor="text-emerald-500" icon="fa-calendar-days" iconBg="bg-emerald-100 text-emerald-600" />
        <MetricCard label="Collection rate"     value="87%"      sub="+3% MoM"         subColor="text-emerald-500" icon="fa-chart-line"   iconBg="bg-blue-100 text-blue-600" />
      </div>

      {/* Chart + AI plan */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        <div className="stat-card rounded-2xl p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-1">A/R Aging Breakdown</h3>
          <p className="text-xs text-gray-400 mb-4">Outstanding balance by age bucket</p>
          <div className="h-52">
            <canvas ref={chartRef}></canvas>
          </div>
        </div>

        <div className="stat-card rounded-2xl p-5 border border-indigo-500/20">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-xl bg-blue-600 flex items-center justify-center text-white text-xs">
              <i className="fas fa-robot"></i>
            </div>
            <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>AI Collection Plan</h3>
            <span className="ml-auto text-[10px] bg-blue-600 text-white px-2 py-0.5 rounded-full font-medium">Live</span>
          </div>
          <ul className="space-y-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
            {[
              { icon: 'fa-circle-xmark', color: 'text-red-500',     text: 'Apply Dunning Level 4 to >90d tail ($15,900)' },
              { icon: 'fa-lock',         color: 'text-amber-500',   text: 'Place credit hold on C10025 ($34k exposure)' },
              { icon: 'fa-handshake',    color: 'text-emerald-500', text: 'Offer 2% early settlement for 61–90d bucket' },
            ].map(({ icon, color, text }) => (
              <li key={text} className="flex items-start gap-2.5">
                <i className={`fas ${icon} ${color} mt-0.5 flex-shrink-0`}></i>
                <span>{text}</span>
              </li>
            ))}
          </ul>
          <div className="mt-4 pt-3" style={{ borderTop: '1px solid var(--glass-border)' }}>
            <p className="text-xs font-medium flex items-center gap-1.5 text-indigo-500">
              <i className="fas fa-circle-info"></i>
              Confidence: 94% · Based on 312 SAP B1 docs
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
